import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { PageHeader, LinkButton, Table, StatusBadge, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { ExportCsvButton } from "@/components/admin/ExportCsvButton";
import { formatDate, formatScore } from "@/lib/format";
import { Loader2, Play, DollarSign } from "lucide-react";

export default function AdminBrandsList() {
  const qc = useQueryClient();
  // { brandId: { runId, paid: bool } }
  const [activeRuns, setActiveRuns] = useState({});
  const { sort, toggleSort, sortRows } = useTableSort();

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ["admin-brands"],
    queryFn: async () => (await api.get("/admin/brands")).data,
  });

  const { data: scraperStatus } = useQuery({
    queryKey: ["scraperapi-status"],
    queryFn: async () => (await api.get("/admin/scraperapi/status")).data,
  });

  // Poll active runs and show toasts on completion
  useQuery({
    queryKey: ["polling-active-crawls", Object.keys(activeRuns).map((k) => `${k}:${activeRuns[k].runId}`).join(",")],
    queryFn: async () => {
      const entries = Object.entries(activeRuns);
      if (!entries.length) return [];
      const results = await Promise.all(
        entries.map(([brandId, { runId, paid }]) =>
          api.get(`/admin/crawl-runs/${runId}`).then((r) => ({ brandId, runId, paid, run: r.data })).catch(() => null)
        )
      );
      let changed = false;
      const next = { ...activeRuns };
      for (const item of results) {
        if (!item) continue;
        const s = item.run.status;
        if (["completed", "completed_with_errors", "failed"].includes(s)) {
          const brandName = item.run.brand_name || "Brand";
          const tierLabel = item.paid ? "Paid crawl" : "Crawl";
          if (s === "failed") {
            const rawError = item.run.error_message || "blocked";
            // Trim noisy internal diagnostics — keep the first sentence only
            const shortError =
              rawError.includes("403") || rawError.toLowerCase().includes("blocking")
                ? "site blocked the request"
                : rawError.length > 80
                ? rawError.slice(0, 80).replace(/\n.*$/s, "") + "…"
                : rawError;
            if (item.paid) {
              toast.error(`${brandName}: paid crawl failed — ${shortError}`, { duration: 8000 });
            } else if (scraperStatus?.configured) {
              toast.error(
                `${brandName}: free crawl failed (${shortError}). Try the "$ Paid" button to retry through ScraperAPI.`,
                { duration: 9000 }
              );
            } else {
              toast.error(
                `${brandName}: free crawl failed (${shortError}). Set a ScraperAPI key in Admin → Settings → Crawler to enable the paid-proxy fallback.`,
                { duration: 9000 }
              );
            }
          } else {
            const newCount = item.run.new_products_found || 0;
            toast.success(`${brandName}: ${tierLabel} ${s === "completed" ? "completed" : "completed with errors"} — ${newCount} new product(s)`);
          }
          delete next[item.brandId];
          changed = true;
        }
      }
      if (changed) {
        setActiveRuns(next);
        qc.invalidateQueries({ queryKey: ["admin-brands"] });
      }
      return results;
    },
    enabled: Object.keys(activeRuns).length > 0,
    refetchInterval: 3000,
  });

  async function runCrawler(brand, paid = false) {
    if (!brand.crawler_start_url && !brand.website_url) {
      toast.error(`${brand.name} has no crawler start URL — set it in Edit first.`);
      return;
    }
    if (paid && !scraperStatus?.configured) {
      toast.error("ScraperAPI key not configured. Set it in Admin → Settings → Crawler.");
      return;
    }
    try {
      const res = await api.post(
        `/admin/brands/${brand.id}/crawler/run`,
        null,
        { params: paid ? { use_scraperapi: true } : {} }
      );
      setActiveRuns((s) => ({ ...s, [brand.id]: { runId: res.data.crawl_run_id, paid } }));
      toast.success(`${paid ? "Paid" : "Free"} crawler started for ${brand.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start crawler");
    }
  }

  const paidConfigured = !!scraperStatus?.configured;

  const sortedBrands = sortRows(brands, (b, key) => {
    if (key === "sustainability_score") return b.sustainability_score ?? null;
    if (key === "total_reviewed_items") return b.total_reviewed_items || 0;
    if (key === "total_approved_items") return b.total_approved_items || 0;
    if (key === "pending_crawler_results_count") return b.pending_crawler_results_count || 0;
    if (key === "is_public") return b.is_public ? 1 : 0;
    if (key === "last_crawled_at") return b.last_crawled_at ? new Date(b.last_crawled_at).getTime() : null;
    return b[key] ?? null;
  });

  return (
    <div data-testid="admin-brands-list">
      <PageHeader
        title="Brands"
        subtitle={`${brands.length} brand${brands.length === 1 ? "" : "s"}${paidConfigured ? " · ScraperAPI ready" : " · ScraperAPI not configured"}`}
        actions={<div className="flex gap-2"><ExportCsvButton endpoint="/admin/brands/export.csv" filename="brands.csv" testid="export-brands-csv" /><LinkButton to="/admin/brands/new">+ New brand</LinkButton></div>}
      />
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="Brand" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Country" sortKey="country" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Score" sortKey="sustainability_score" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Reviewed" sortKey="total_reviewed_items" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Approved" sortKey="total_approved_items" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Pending review" sortKey="pending_crawler_results_count" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Public" sortKey="is_public" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Last crawled" sortKey="last_crawled_at" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Actions" />
            </tr>
          </thead>
          <tbody>
            {sortedBrands.map((b) => {
              const active = activeRuns[b.id];
              const isRunning = !!active;
              const isPaidRunning = isRunning && active.paid;
              const isFreeRunning = isRunning && !active.paid;
              return (
                <tr key={b.id} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]">
                  <td className="px-3 py-3 font-medium">{b.name}</td>
                  <td className="px-3 py-3">{b.country || "—"}</td>
                  <td className="px-3 py-3 font-mono">{formatScore(b.sustainability_score)}</td>
                  <td className="px-3 py-3 font-mono">{b.total_reviewed_items || 0}</td>
                  <td className="px-3 py-3 font-mono text-green-700">{b.total_approved_items || 0}</td>
                  <td className="px-3 py-3 font-mono">
                    {b.pending_crawler_results_count > 0 ? (
                      <Link
                        to={`/admin/brands/${b.id}/crawler-results?status=new`}
                        className="text-forest underline underline-offset-4"
                        data-testid={`brand-pending-review-${b.id}`}
                      >
                        {b.pending_crawler_results_count}
                      </Link>
                    ) : (
                      <span className="text-[#6C757D]">0</span>
                    )}
                  </td>
                  <td className="px-3 py-3">{b.is_public ? "Yes" : "No"}</td>
                  <td className="px-3 py-3 text-xs">{formatDate(b.last_crawled_at)}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <button
                      onClick={() => runCrawler(b, false)}
                      disabled={isRunning || !b.crawler_enabled}
                      className="inline-flex items-center gap-1 bg-[#212529] text-white rounded px-2.5 py-1 text-xs hover:bg-[#343A40] disabled:opacity-60 disabled:cursor-not-allowed mr-1.5"
                      data-testid={`crawl-brand-${b.id}`}
                      title={!b.crawler_enabled ? "Crawler is disabled for this brand" : "Free crawler (httpx → patchright)"}
                    >
                      {isFreeRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      {isFreeRunning ? "Crawling…" : "Crawl"}
                    </button>
                    <button
                      onClick={() => runCrawler(b, true)}
                      disabled={isRunning || !b.crawler_enabled || !paidConfigured}
                      className="inline-flex items-center gap-1 bg-white border border-[#212529] text-[#212529] rounded px-2.5 py-1 text-xs hover:bg-[#F1F3F5] disabled:opacity-50 disabled:cursor-not-allowed mr-2"
                      data-testid={`crawl-paid-brand-${b.id}`}
                      title={
                        !paidConfigured
                          ? "ScraperAPI key not configured — set it in Admin → Settings → Crawler"
                          : !b.crawler_enabled
                          ? "Crawler is disabled for this brand"
                          : "Paid crawler via ScraperAPI: 2 standard attempts (~10 credits each) then escalates to ultra_premium (~75 credits) on the 3rd try if needed. Failed requests are not billed."
                      }
                    >
                      {isPaidRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                      {isPaidRunning ? "Crawling…" : "Paid"}
                    </button>
                    <Link to={`/admin/brands/${b.id}`} className="text-sm underline" data-testid={`edit-brand-${b.id}`}>Edit</Link>
                    {" · "}
                    <Link to={`/admin/brands/${b.id}/crawler-results`} className="text-sm underline">Results</Link>
                  </td>
                </tr>
              );
            })}
            {!isLoading && brands.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-[#6C757D]">No brands yet.</td></tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
